import { autorun, makeAutoObservable, observable, runInAction, toJS, when } from "mobx";

import { isEqual } from "lodash";
import hash from "object-hash";
import {
  exportSearchResults,
  fetchCompoundActivity,
  fetchGraphData as fetchGraphDataWithRetry,
  fetchSearch,
  fetchSubstructureMatches,
} from "../queries/search";
import { fetchEChains } from "../queries/echains";
import { fetchEntities, fetchMembers, fetchSuggestions } from "../queries/autosuggest";
import {
  CORSError,
  NotAuthorized,
  SearchCanceled,
  SearchError,
  ServerError,
  ServerOfflineError
} from "../queries/errors";
import { extractTemplates } from "../markdown/templates-utils";
import { TAXID_NONE } from "./AppStatusStore";
import SearchContext from "../lib/SearchContext";
import { captureMessage, extractLabel, getBestName, isPlexId, saveCustomLabel } from "../lib/utils";
import { patchEntity } from "../lib/patchEntity";
import { plainDescription } from "../lib/sourceMetadata";
import { toast } from "react-toastify";
import { looseMatchRegExp, normalize } from "../markdown/utils";
import { DEV } from "../lib/configMgr";
import Analytics from "../lib/Analytics";
import { evidenceLink, generateExternalLinks } from "../lib/links";

/**
 * Derive the stable UUID that would be assigned to a search with the given canonical URL.
 * A history_id matching this value indicates a plain (no-conversation) search item.
 */
export const searchUUIDFromURL = (url) => {
  const urlHash = hash(url);
  return `${urlHash.slice(0,8)}-${urlHash.slice(8,12)}-${urlHash.slice(12,16)}-${urlHash.slice(16,20)}-${urlHash.slice(20,32)}`;
};

const DEFAULT_PAGE_SIZE = 250;
// Prevent performance degradation
const DEFAULT_DOWNLOAD_LIMIT = 10000;
const DEFAULT_CATEGORY_KEY = "target";
const LOCAL_PARAMS = new Set(["tab", "filter", "location", "q", "gtc", "history_id"]);

// Use a slightly higher default than the server default
export const DEFAULT_CLIENT_SIM_THRESHOLD = 0.75;
// Default to pseudo-similarity on
export const DEFAULT_CLIENT_FIND_RELATED = false;
// For chem/sim/pubchem
export const MIN_SIM_THRESHOLD = 0.55;
// For ECFP*
export const MIN_OTHER_THRESHOLD = 0.1;
export const DEFAULT_COMPOUND_SIMTYPE = "sim";
export const DEFAULT_MIN_EXPANSION = 5;
export const DEFAULT_SIM_METHOD = "tanimoto";
export const DEFAULT_DSCONFIG = 0;


export const MIN_FILTER_LENGTH = 2;

const CLEAR_FILTER_ON_TAB_CHANGE = false;
// 'faers' is excluded because patchEntity sets entity.faers = "FAERS" on every
// adverse-event entity; without this the literal text filter "faers" would match
// all of them regardless of content (PLEX-4044).
const IGNORE_KEYS = new Set(['rank', 'category', 'baseCategory', 'url', 'tags', 'links', 'categoryName', 'categoryLabel', 'faers']);

// Predefined tab positions; anything not here gets tacked onto the end
const TAB_ORDINAL = {
  target: 1,
  compound: 2,
  pathway: 3,
  biomarker: 4,
  gds: 5,
  "proteomic-profile": 6,
  // obsolete
  gwps: 7,
  orcs: 8,
  "cell-line": 9,
  disease: 10,
  publication: 11,
  patent: 12,
  org: 13,
  moa: 14,
  "genome-position": 15,
  motif: 16,
  // Never display these base categories
  geneset: -1,
  cpdset: -1,
  // Category for HMDB, not yet published
  mp: -1,
  // PLEX-4044: faers folded into the Diseases tab; never show a separate FAERS tab
  // (defensive — the backend no longer returns a faers category either).
  faers: -1,
};

const filterPredicate = (pattern) => content => {
  const recursivelyFilter = obj => {
    // Recursively examine aggregate objects
    if (obj === undefined || obj === null) {
      return false;
    }
    if (obj?.reduce) {
      return obj.reduce((result, el) => result || recursivelyFilter(el), false);
    }
    if (typeof(obj) === "object") {
      return Object.entries(obj).reduce((result, [key, value]) => {
        // Kind of a hack, ideally we'd check which fields are referenced in the markdown,
        // but that can be expensive if not precomputed.  We also need to maintain the filtered
        // data list for other operations
        return result || (!IGNORE_KEYS.has(key) && recursivelyFilter(value));
      }, false);
    }
    return pattern.test(normalize(obj.toString()));
  };
  return recursivelyFilter(content);
};

class SearchSessionStore {

  _pendingIdLookups = new Map();
  _idLookups = new Map();
  _failedIdLookups = new Set();
  _appStatusStore = null;
  _tabs = [];
  _categories = {};
  _categoryTotals = new Map();
  _categoryResults = [];
  _clientSearchKey = null;
  _currentCategory = null;
  _chatContext = null;
  _datasets = {};
  _defaultSimThresholds = {};
  _dsConfigDatasets = {};
  _fetchingMetadata = false;
  _fetchingDsConfigMetadata = false;
  _filterCache = new Map();
  _filterString = "";
  _downloadedResults = new Map();
  _entities = {};
  _isFetchingMore = false;
  _isFilterMatched = false;
  _lastError = "";
  _metadata = null;
  _defaultSearchParams = {};
  _params = {};
  _preferredNames = {};
  _queryEntities = [];
  _queuedSearchParams = {};
  _resolvingTerms = [];
  _downloadLimit = DEFAULT_DOWNLOAD_LIMIT;
  _downloadLimitReached = false;
  _searchProgress = "";
  _searchResultAnnotation = "";
  _searchStreamId = null;
  _streamRetryCount = 0;
  _graphTargetCategory = null;
  _templates = null;
  _userStore = null;
  _clientDefaultParams = null;
  _currentSearchConversationId = null; // UUID of current search conversation for analyst modal
  _serverSearchId = null; // Server-side search UUID (for activity API)
  _activityAvailable = false; // Whether target activity has been computed for this search
  _activityComputing = false; // Whether activity computation is in progress
  _activityProgress = null; // {phase, computed, total} during computation
  _substructureQuery = null;
  _substructureMatches = null;
  _substructureLoading = false;
  // Re-entrancy guard for saveSearchToHistory, not observable: the autosave autorun
  // below reads it as a plain lock, not a reactive dependency. See WEBAPP-FC.
  _savingToHistory = false;

  constructor(userStore, appStatusStore, resultsBatchSize = DEFAULT_PAGE_SIZE) {
    makeAutoObservable(this, { _savingToHistory: false });

    this.userStore = userStore;
    this.appStatusStore = appStatusStore;
    this.historyStore = null;
    this.resultsBatchSize = resultsBatchSize;
    this.clear()
    autorun(() => {
      //console.log("autorun fetchMetadata", this.datasets, this.userStore.isAuthorized);
      // Run whenever one of these observable items changes
      if (Object.entries(this.datasets).length === 0
          && !this.lastError
          && this.userStore.isAuthorized) {
        this.appStatusStore.fetchMetadata(userStore.isAdmin || userStore.isPowerUser, userStore.authData)
          .then(({
                   default_search_params: {
                     density_expansion: densityExpansion = null,
                     find_related: findRelated = false,
                     imap: dsConfig = 0,
                     indirect = false,
                     min_expansion: minExpansion = null,
                     sim_threshold: simThreshold = DEFAULT_CLIENT_SIM_THRESHOLD,
                     sim_type: simType = DEFAULT_COMPOUND_SIMTYPE,
                     sim_method: simMethod = DEFAULT_SIM_METHOD,
                     sim_substructure: simSubstructure = false,
                     page_size: pageSize = DEFAULT_PAGE_SIZE,
                     xf = null,
                   } = {},
                   preferred_names: preferredNames = {},
                   sim_types_available: defaultSimTypeThresholds = {},
                 }) => {
            runInAction(() => {
              this.defaultSearchParams = {
                densityExpansion,
                dsConfig,
                findRelated,
                indirect,
                minExpansion,
                simThreshold,
                simMethod,
                simSubstructure,
                pageSize,
                xf,
              };
              if (simType !== "default") {
                this.defaultSearchParams.simType = simType;
              }
              this.preferredNames = preferredNames || {};
              this.defaultSimThresholds = {...defaultSimTypeThresholds};
            });
          })
          .then(() => this.appStatusStore.fetchDatasetMetadata(this.authData)
            .then((datasets) => {
              runInAction(() => this.datasets = datasets);
            })
          )
          .catch((error) => {
            this.clear("Could not obtain backend info");
          })
      }
    });
    autorun(() => {
      // Run whenever one of these observable items changes
      //console.log("autorun fetchDsConfigMetadata", this.dsConfig, this.metadata, userStore.isAuthorized);
      if (this.dsConfig != null
          && !this.lastError
          && this.metadata === null
          && userStore.isAuthorized) {
        this.fetchDsConfigMetadata(this.dsConfig)
          .catch(error => {
            this.clear("Could not obtain graph info");
          });
      }
    });
    autorun(() => {
      // Auto-save search to history when search completes
      // Skip if currentSearchConversationId is already set (loaded from history), or if a
      // save is already in flight — saveSearchToHistory nulls currentSearchConversationId on
      // failure to allow a retry, and that write re-enters this autorun synchronously; without
      // the _savingToHistory guard this fires a nested, re-entrant saveSearchToHistory() call
      // while the previous one's currentSearchConversationId setter is still unwinding, which
      // MobX rejects with error 33 ("setter ... trying to update itself"). See WEBAPP-FC.
      console.debug('[SearchSessionStore autorun] Checking save conditions', {
        hasFullResults: this.hasFullResults,
        hasError: !!this.lastError,
        hasConversationId: !!this.currentSearchConversationId,
        savingToHistory: this._savingToHistory,
        willSave: this.hasFullResults && !this.lastError && !this.currentSearchConversationId && !this._savingToHistory
      });
      if (this.hasFullResults && !this.lastError && !this.currentSearchConversationId && !this._savingToHistory) {
        this.saveSearchToHistory();
      }
    });
  }

  cancelSearch = () => {
    // Don't reset currentCategory here - it's a UI preference that should persist
    // or be explicitly set by the caller (e.g., from URL tab parameter)
    this.clientSearchKey = null;
    this.searchStreamId = null;
    this._streamRetryCount = 0;
    this.awaitingInitialResults = false;
    this.retainResultsLayout = false;
    this.currentSearchConversationId = null;
    this._savingToHistory = false;
    this.isFetchingMore = false;
    this.searchProgress = "";
    this.downloadLimit = DEFAULT_DOWNLOAD_LIMIT;
    this.downloadLimitReached = false;
    this.searchResultAnnotation = "";
    this.lastError = null;
    this.categoryTotals.clear();
    this.downloadedResults.clear();
    this.categoryResults = [];
    this._substructureQuery = null;
    this._substructureMatches = null;
    this._substructureLoading = false;
    this._serverSearchId = null;
    this._activityAvailable = false;
    this._activityComputing = false;
    this._activityProgress = null;
  };

  // Cache of completed search results, keyed by search identity (history_id, or a
  // canonical-URL-derived UUID for plain searches without a conversation). Populated by
  // clear() just before it wipes downloadedResults/categoryTotals, so that navigating
  // away (e.g. to the landing page) and back to the *same* search/chat can restore the
  // prior results instead of unconditionally relaunching a fresh search. See PLEX-6054.
  _resultsCache = new Map();

  get resultsCacheKey() {
    return this.currentSearchConversationId
      || searchUUIDFromURL(SearchContext.getAppSearchURL(this.params, {}, {withLabels: true}));
  }

  hasCachedResults = (key) => this._resultsCache.has(key);

  cacheCurrentResults = () => {
    // Only cache complete, error-free result sets — no point caching partial/failed state.
    if (!this.hasFullResults || this.lastError) {
      return;
    }
    const key = this.resultsCacheKey;
    this._resultsCache.set(key, {
      params: {...this.params},
      queryEntities: toJS(this.queryEntities),
      downloadedResults: new Map(Array.from(this._downloadedResults.entries())
        .map(([cat, items]) => [cat, items.slice()])),
      categoryTotals: new Map(this._categoryTotals),
      currentCategory: this.currentCategory,
      serverSearchId: this._serverSearchId,
      activityAvailable: this._activityAvailable,
      currentSearchConversationId: this.currentSearchConversationId,
    });
  };

  // Restore a previously cached result set (see cacheCurrentResults). Returns true on a
  // cache hit (and restores state synchronously), false otherwise so the caller can fall
  // back to launching a fresh search.
  restoreCachedResults = (key) => {
    const snapshot = this._resultsCache.get(key);
    if (!snapshot) {
      return false;
    }
    runInAction(() => {
      this.params = snapshot.params;
      this.queryEntities = snapshot.queryEntities;
      // Mutate the existing observable Map in place (mirrors the pattern used elsewhere
      // in this store) rather than reassigning _downloadedResults to a new instance.
      this._downloadedResults.clear();
      snapshot.downloadedResults.forEach((items, cat) => {
        this._downloadedResults.set(cat, observable(items.slice(), {deep: false}));
      });
      this._categoryTotals.clear();
      snapshot.categoryTotals.forEach((total, cat) => this._categoryTotals.set(cat, total));
      this._serverSearchId = snapshot.serverSearchId;
      this._activityAvailable = snapshot.activityAvailable;
      this.currentSearchConversationId = snapshot.currentSearchConversationId;
      this.currentCategory = snapshot.currentCategory;
      this.clientSearchKey = this.createSearchKey(snapshot.params);
      this.awaitingInitialResults = false;
      this.updateCategoryResults(false, true);
    });
    return true;
  };

  clear = (errorMessage = null) => {
    if (!errorMessage) {
      this.cacheCurrentResults();
    }
    this.cancelSearch()
    this.resolvingTerms = [];
    this.failedIdLookups.clear();
    this.gexf = null;
    this.graphml = null;
    this._filterCache.clear();
    this.filterString = "";
    this._substructureQuery = null;
    this._substructureMatches = null;
    this._substructureLoading = false;
    this.isFilterMatched = true;
    this.preferredNames = {};
    this.clientDefaultParams = {
      simThreshold: DEFAULT_CLIENT_SIM_THRESHOLD,
      findRelated: DEFAULT_CLIENT_FIND_RELATED,
      pageSize: DEFAULT_PAGE_SIZE,
    };
    // Reset category to default on full clear (not during cancelSearch which preserves it for URL loading)
    this.currentCategory = DEFAULT_CATEGORY_KEY;
    if (errorMessage) {
      this.searchResultAnnotation = errorMessage;
      this.lastError = errorMessage;
      this.datasets = {};
      this.categories = {};
      this.dsConfigDatasets = {};
      this.metadata = null;
    }
    else {
      // Clear only query IDs, leave other settings as-is
      this.queuedSearchParams = {};
      this.queryEntities.length = 0;
      this.setParamsToDefault({...this.params, searchIDs: null, queryEntities: null});
    }
  }

  setParamsToDefault = (params = {}) => {
    this.params = params;
    this.awaitingInitialResults = false;
  };

  createSearchKey = (input, ts = new Date().getTime()) => hash({...input, ts: ts});

  get idLookups() {
    return this._idLookups;
  }

  get pendingIdLookups() {
    return this._pendingIdLookups;
  }

  get currentSearchConversationId() {
    return this._currentSearchConversationId;
  }

  set currentSearchConversationId(value) {
    this._currentSearchConversationId = value;
  }

  get serverSearchId() {
    return this._serverSearchId;
  }

  set serverSearchId(id) {
    this._serverSearchId = id;
  }

  get activityAvailable() {
    return this._activityAvailable;
  }

  set activityAvailable(v) {
    this._activityAvailable = v;
  }

  get activityComputing() {
    return this._activityComputing;
  }

  set activityComputing(v) {
    this._activityComputing = v;
  }

  get activityProgress() {
    return this._activityProgress;
  }

  computeActivities = async (onComplete = null) => {
    if (!this._serverSearchId || this._activityComputing || this._activityAvailable) {
      return;
    }
    // Set synchronously before first await so concurrent calls hit the guard immediately
    runInAction(() => { this._activityComputing = true; this._activityProgress = null; });
    const clientSearchKey = this.clientSearchKey;
    try {
      const result = await fetchCompoundActivity(
        this._serverSearchId,
        this.authData,
        event => runInAction(() => { this._activityProgress = event; }),
      );
      if (result?.status !== 200 || clientSearchKey !== this.clientSearchKey) {
        return;
      }
      // Activity is now cached server-side. Re-fetch all downloaded compound pages so
      // annotate_ranked_results can merge the activity data into the entities.
      const response = await fetchSearch(
        {...this.params, pageToken: null},
        this.authData,
        {searchType: "search", isCanceled: () => clientSearchKey !== this.clientSearchKey},
      );
      if (clientSearchKey !== this.clientSearchKey) return;
      const {results, page_token: pageToken} = response;
      if (results) {
        runInAction(() => {
          this._activityAvailable = true;
          Object.entries(results).forEach(([cat, items]) => {
            if (cat !== "compound") return;
            const downloaded = this._downloadedResults.get(cat);
            if (!downloaded) return;
            const byId = new Map(items.map(item => [item.entity?.id, item]));
            downloaded.forEach((item, idx) => {
              const fresh = byId.get(item.entity?.id);
              if (fresh?.entity?.activity) {
                const activity = Object.entries(fresh.entity.activity);
                const updatedEntity = {...item.entity, activity};
                this._entities[updatedEntity.id] = updatedEntity;
                downloaded.splice(idx, 1, {...item, entity: updatedEntity});
              }
            });
          });
          this.updateCategoryResults(false, true);
        });
        if (pageToken) {
          this._refreshActivityPages(clientSearchKey, pageToken);
        }
      }
      if (onComplete) {
        onComplete();
      } else {
        toast.success("Target activity computation complete");
      }
    } catch (e) {
      console.error("Failed to compute target activity", e);
      toast.error("Failed to compute target activity");
    } finally {
      runInAction(() => { this._activityComputing = false; this._activityProgress = null; });
    }
  };


  _refreshActivityPages = async (clientSearchKey, pageToken) => {
    while (pageToken && clientSearchKey === this.clientSearchKey) {
      const response = await fetchSearch(
        {...this.params, pageToken},
        this.authData,
        {searchType: "search", isCanceled: () => clientSearchKey !== this.clientSearchKey},
      );
      if (clientSearchKey !== this.clientSearchKey) return;
      const {results, page_token: nextPageToken} = response;
      if (results) {
        runInAction(() => {
          Object.entries(results).forEach(([cat, items]) => {
            if (cat !== "compound") return;
            const downloaded = this._downloadedResults.get(cat);
            if (!downloaded) return;
            const byId = new Map(items.map(item => [item.entity?.id, item]));
            downloaded.forEach((item, idx) => {
              const fresh = byId.get(item.entity?.id);
              if (fresh?.entity?.activity) {
                const activity = Object.entries(fresh.entity.activity);
                const updatedEntity = {...item.entity, activity};
                this._entities[updatedEntity.id] = updatedEntity;
                downloaded.splice(idx, 1, {...item, entity: updatedEntity});
              }
            });
          });
          this.updateCategoryResults(false, true);
        });
      }
      pageToken = nextPageToken;
    }
  };

  get failedIdLookups() {
    return this._failedIdLookups;
  }

  get defaultSimThresholds() {
    return toJS(this._defaultSimThresholds);
  }

  set defaultSimThresholds(thresholds) {
    this._defaultSimThresholds = observable(thresholds);
  }

  set downloadLimit(limit) {
    this._downloadLimit = Math.max(DEFAULT_DOWNLOAD_LIMIT, limit);
  }

  get downloadLimit() {
    return this._downloadLimit;
  }

  set downloadLimitReached(limited) {
    this._downloadLimitReached = limited;
  }

  get downloadLimitReached() {
    return this._downloadLimitReached;
  }

  get gexf() {
    return this._gexf;
  }

  set gexf(data) {
    this._gexf = data;
  }

  get graphml() {
    return this._graphml;
  }

  set graphml(data) {
    this._graphml = data;
  }

  get appStatusStore() {
    return this._appStatusStore;
  }

  set appStatusStore(s) {
    this._appStatusStore = s;
  }

  get fetchingMetadata() {
    return this._fetchingMetadata;
  }

  set fetchingMetadata(fetching) {
    this._fetchingMetadata = fetching;
  }

  get fetchingDsConfigMetadata() {
    return this._fetchingDsConfigMetadata;
  }

  set fetchingDsConfigMetadata(fetching) {
    this._fetchingDsConfigMetadata = fetching;
  }

  get categories() {
    return toJS(this._categories);
  }

  set categories(categories) {
    this._categories = categories;
  }

  get tabs() {
    // Dynamically generate tabs based on current categories and results
    if (Object.keys(this.categories).length === 0) {
      return [];
    }

    const tabs = [
      {
        key: "graph",
        title: "Graph",
        description: "Search results graph",
        name: "graph",
        className: "fi fi-rs-network-analytic",
        disabled: !this.hasNonQueryCategoryResults
      }
    ];

    tabs.push(...Object.entries(this.categories)
      .reduce((acc, [key, value], idx) => {
        if (TAB_ORDINAL[key] !== -1) {
          acc.push({
                     key: key,
                     title: value.tab_name,
                     description: value.description || value.tab_name,
                     name: value.name,
                     ordinal: TAB_ORDINAL[key] || (Object.keys(TAB_ORDINAL).length + idx),
                   });
        }
        return acc;
      }, [])
      .sort((a, b) => a.ordinal - b.ordinal));

    return tabs;
  }

  _awaitingInitialResults = false;
  _retainResultsLayout = false;

  get awaitingInitialResults() {
    return this._awaitingInitialResults;
  }

  set awaitingInitialResults(waiting) {
    this._awaitingInitialResults = waiting;
  }

  get retainResultsLayout() {
    return this._retainResultsLayout;
  }

  set retainResultsLayout(val) {
    this._retainResultsLayout = val;
  }

  get searchProgress() {
    return this._searchProgress;
  }

  set searchProgress(s) {
    this._searchProgress = s;
  }

  get searchStreamId() {
    return this._searchStreamId;
  }

  set searchStreamId(id) {
    this._searchStreamId = id;
  }

  get userStore() {
    return this._userStore;
  }

  set userStore(s) {
    this._userStore = s;
  }

  get defaultsLoaded() {
    return !isEqual(this.defaultSearchParams, {});
  }

  get dsConfig() {
    return this.paramsWithDefaults.dsConfig || 0;
  }

  get simType() {
    return this.paramsWithDefaults.simType;
  }

  get simThreshold() {
    return this.paramsWithDefaults.simThreshold;
  }

  get simMethod() {
    return this.paramsWithDefaults.simMethod;
  }

  get simSubstructure() {
    return !!this.paramsWithDefaults.simSubstructure;
  }

  get findRelated() {
    return !!this.paramsWithDefaults.findRelated;
  }

  get indirect() {
    return !!this.paramsWithDefaults.indirect;
  }

  get densityExpansion() {
    return this.paramsWithDefaults.densityExpansion;
  }

  get searchIDs() {
    return this.params.searchIDs || (this.params.queryEntities || []).map(qe => qe.id);
  }

  get resolvingTerms() {
    return this._resolvingTerms?.length && toJS(this._resolvingTerms);
  }

  set resolvingTerms(terms) {
    this._resolvingTerms = terms;
  }

  get queryEntities() {
    return this._queryEntities;
  }

  set queryEntities(qe) {
    if (!_.isEqual(toJS(qe), toJS(this.queryEntities))) {
      const unique = new Set();
      this._queryEntities = qe.filter(el => !unique.has(el.id) && unique.add(el.id));
    }
  }

  get isFetchingMore() {
    return this._isFetchingMore;
  }

  set isFetchingMore(s) {
    this._isFetchingMore = s;
  }

  get preferredNames() {
    return this._preferredNames;
  }

  set preferredNames(pn) {
    this._preferredNames = pn;
  }

  get params() {
    return toJS(this._params);
  }

  set params(p) {
    this._params = p;
  }

  get defaultSearchParams() {
    return toJS(this._defaultSearchParams);
  }

  set defaultSearchParams(s) {
    this._defaultSearchParams = s;
  }

  get paramsWithDefaults() {
    if (!this.defaultsLoaded) {
      //console.warn("defaultSearchParams not yet set");
    }
    const defaults = {
      ...this.defaultSearchParams,
      ...this.clientDefaultParams,
    }
    // Adjust the sim threshold if the sim type is set
    if (this.params.simType && !this.params.simThreshold) {
      defaults.simThreshold = this.defaultSimThresholds[this.params.simType];
    }
    return {
      ...defaults,
      ...this.params,
      queryEntities: toJS(this.queryEntities),
    };
  }

  resolveSourceLinks = async (ids) => {
    if (!ids?.length) return {};
    await when(() => Object.keys(this._datasets).length > 0);
    const idLookup = await this.resolveIds(ids);
    // Snapshot once — this.datasets calls toJS() and would clone the full map per entity
    const datasets = this.datasets;
    return Object.entries(idLookup)
      .reduce((result, [id, entity]) => {
        if (id === entity) {
          // Not an entity. An evidence record's ID links as its dataset's evidence (PLEX-6500).
          const evidence = evidenceLink(id, datasets);
          result[id] = evidence
            ? {name: evidence.name, shortName: null, url: evidence.url, category: null, failed: false, evidence: true}
            : {name: id, shortName: null, url: id, category: null, failed: true};
          return result;
        }

        const links = generateExternalLinks(entity, datasets);
        const name = getBestName(entity);
        const shortName = getBestName(entity, true);
        result[id] = {
          name,
          shortName: shortName !== name ? shortName : null,
          url: links?.length ? links[0].url : id,
          category: entity.category,
          // PLEX-5920: the source card shows this rather than the linked page's own metadata
          description: plainDescription(entity.description || entity.abstract),
          failed: false
        };
        return result;
      }, {});
  };

  // Micro-batch queue: accumulates IDs from concurrent resolveIds calls within the same
  // event-loop tick, then fires a single fetchEntities for all of them at once.
  _pendingBatch = null; // { ids: Set, resolvers: [{ids, resolve, reject}], promise: Promise }

  _flushBatch = () => {
    const batch = this._pendingBatch;
    this._pendingBatch = null;
    const batchIds = Array.from(batch.ids);
    const request = this.fetchEntities(batchIds)
      .then(entities => {
        batchIds.forEach((id, index) => {
          const entity = entities[index];
          if (entity !== null) {
            this.idLookups.set(entity.id, entity);
            this.pendingIdLookups.delete(entity.id);
            this.pendingIdLookups.delete(id);
            this.failedIdLookups.delete(entity.id);
            this.failedIdLookups.delete(id);
          } else {
            if (/^(smiles|query):/.test(id)) {
              const label = id.replace(/^(smiles|query):/, "");
              this.idLookups.set(id, { id, label, category: null, synthetic: true });
              this.pendingIdLookups.delete(id);
            } else {
              this.failedIdLookups.add(id);
              this.pendingIdLookups.delete(id);
            }
          }
        });
        // Resolve each caller's promise with just their requested IDs
        batch.resolvers.forEach(({ ids: callerIds, resolve }) => {
          const result = callerIds.reduce((acc, id) => {
            const found = toJS(this.idLookups.get(id));
            return { [id]: found || id, ...acc };
          }, {});
          resolve(result);
        });
      })
      .catch(e => {
        batchIds.forEach(id => this.pendingIdLookups.delete(id));
        if (e instanceof ServerOfflineError) {
          batch.resolvers.forEach(({ reject }) => reject(e));
        } else {
          batch.resolvers.forEach(({ ids: callerIds, resolve }) => {
            resolve(callerIds.reduce((acc, id) => ({ [id]: id, ...acc }), {}));
          });
        }
      });
    batchIds.forEach(id => this.pendingIdLookups.set(id, request));
  }

  resolveIds = async (ids) => {
    if (!ids?.length) {
      return {};
    }
    const required = new Set(ids.filter(id => isPlexId(id)));
    // Wait for any already in-flight requests for these IDs
    await Promise.all(Array.from(this.pendingIdLookups.entries())
                        .filter(([id]) => required.has(id))
                        .map(([, p]) => p));
    const resolved =
      Object.fromEntries(Array.from(this.idLookups.entries())
                           .filter(([id]) => required.has(id)));
    const failed = new Set([...this.failedIdLookups].filter(x => required.has(x)));
    const resolvedKeys = new Set(Object.keys(resolved));
    const missing = new Set([...required].filter(x => !resolvedKeys.has(x) && !failed.has(x)));
    failed.forEach(id => { resolved[id] = id; });
    if (!missing.size) {
      return resolved;
    }
    // Enqueue missing IDs into the micro-batch. All resolveIds calls that reach this
    // point within the same event-loop tick share a single fetchEntities request.
    return new Promise((resolve, reject) => {
      if (!this._pendingBatch) {
        this._pendingBatch = { ids: new Set(), resolvers: [] };
        Promise.resolve().then(this._flushBatch);
      }
      missing.forEach(id => this._pendingBatch.ids.add(id));
      this._pendingBatch.resolvers.push({ ids: Array.from(missing), resolve, reject });
    }).then(batchResult => ({ ...resolved, ...batchResult }));
  }

  normalizeSimilarityParams = (searchParams, removeDefaults = true) => {
    // Prioritize settings:
    // disabled > substructure > sim type (minExpansion > simThreshold)
    const params = {...searchParams}
    const defaults = new Set(["org", "set", DEFAULT_COMPOUND_SIMTYPE, "default"]);
    const simKey = this.searchSimKey(params);
    if (defaults.has(simKey) && removeDefaults) {
      delete params.simType;
    }
    if (simKey === "") {
      // similarity disabled
      delete params.simSubstructure;
      delete params.simThreshold;
      delete params.simMethod;
    }
    // Make sure we check for _exactly_ null
    else if (simKey === null) {
      // similarity not applicable
      delete params.simThreshold;
      delete params.simSubstructure;
      delete params.simMethod;
    }
    else if (params.simSubstructure) {
      delete params.simThreshold;
      delete params.simMethod;
      if (params.simType === undefined) {
        delete params.simType;
      }
    }
    else {
      if (simKey === "default" && removeDefaults) {
        delete params.simType;
      }
      if (params.minExpansion) {
        delete params.simThreshold;
      }
      else {
        delete params.minExpansion;
        if (params.simThreshold === this.defaultSimThresholds[simKey]
            && removeDefaults) {
          delete params.simThreshold;
        }
      }
    }
    return params;
  }

  get queuedSearchParams() {
    // Compose "queued" search parameters based on existing search parameters
    // and parameters queued for the next search
    // Include any defaults as explicit parameters
    const queuedQueryEntities = toJS(this._queuedSearchParams?.queryEntities);
    const params= {
      ...this.paramsWithDefaults,
      ...this._queuedSearchParams,
      queryEntities: queuedQueryEntities,
      searchIDs: queuedQueryEntities?.map(x => x.id),
    };
    // Differentiate missing versus blanked query terms
    // null == not set (use current search terms)
    // [] == temporarily blanked
    if (!queuedQueryEntities) {
      params.queryEntities = this.queryEntities;
      params.searchIDs = this.searchIDs;
    }
    return toJS(this.normalizeSimilarityParams(params, false));
  }

  set queuedSearchParams(p) {
    // Maintain an internal list of only those settings which vary from the
    // defaults
    this._queuedSearchParams = p;
  }

  modifySearchParameters = (newParams) => {
    const merged = {...this._queuedSearchParams, ...newParams};
    // Clear DC/neighborhood filters when search terms change
    if (newParams.queryEntities) {
      const newIDs = new Set(newParams.queryEntities.map(e => e.id));
      const currentIDs = new Set(this.searchIDs);
      if (!_.isEqual(newIDs, currentIDs)) {
        merged.directConnectionID = null;
        merged.neighborhood = null;
      }
    }
    this.queuedSearchParams = merged;
  }

  // The search field was emptied. Record the blank, so the queued search and its URL agree
  // with the field: [] means "temporarily blanked" (see queuedSearchParams), which leaves no
  // queuedSearchURL, so Run asks for a search term. Unlike modifySearchParameters this keeps
  // the filters tied to the current terms, since a tab switch brings those terms back
  // (PLEX-6631). Nothing to record before any terms exist, e.g. while a URL is resolving.
  blankQueuedSearchTerms = () => {
    if (!this.queryEntities?.length && !this._queuedSearchParams?.queryEntities?.length) {
      return;
    }
    this.queuedSearchParams = {...this._queuedSearchParams, queryEntities: []};
  }

  // Switching results tabs brings an emptied search field back to the current search's
  // terms (PLEX-T927, PLEX-T928). Terms the user has entered stay queued.
  restoreBlankedSearchTerms = () => {
    const queued = this._queuedSearchParams?.queryEntities;
    if (Array.isArray(queued) && queued.length === 0) {
      const {queryEntities: _, searchIDs: __, ...rest} = this._queuedSearchParams;
      this.queuedSearchParams = rest;
    }
  }

  get hasNewParams() {
    const {queryEntities: _, ...current} = this.canonicalParams(this.params, true);
    const {queryEntities: __, ...queued} = this.canonicalParams(this.queuedSearchParams, true);
    const lhs = JSON.stringify(current);
    const rhs = JSON.stringify(queued);
    return lhs !== rhs;
  }

  get metadata() {
    return toJS(this._metadata);
  }

  set metadata(m) {
    this._metadata = m;
  }

  get datasets() {
    return toJS(this._datasets);
  }

  set datasets(d) {
    this._datasets = d;
  }

  get dsConfigDatasets() {
    return toJS(this._dsConfigDatasets);
  }

  set dsConfigDatasets(d) {
    this._dsConfigDatasets = d || {};
  }

  get templates() {
    return toJS(this._templates);
  }

  set templates(t) {
    this._templates = t;
  }

  get searchResultAnnotation() {
    return this._searchResultAnnotation;
  }

  set searchResultAnnotation(s) {
    this._searchResultAnnotation = s;
  }

  get lastError() {
    return this._lastError;
  }

  set lastError(e) {
    if (e) {
      toast.error(e, {toastId: e, preventDuplicates: true})
    }
    this._lastError = e;
  }

  get isFiltering() {
    return this.filterString.length >= MIN_FILTER_LENGTH;
  }

  get isFilterMatched() {
    return this._isFilterMatched;
  }

  set isFilterMatched(matched) {
    this._isFilterMatched = matched;
  }

  get substructureQuery() {
    return this._substructureQuery;
  }

  get substructureMatches() {
    return this._substructureMatches;
  }

  get substructureLoading() {
    return this._substructureLoading;
  }

  get isSubstructureFiltering() {
    return this._substructureQuery !== null && this._substructureMatches !== null;
  }

  setSubstructureFilter = async (query) => {
    if (!query) {
      this.clearSubstructureFilter();
      return;
    }
    runInAction(() => {
      this._substructureQuery = query;
      this._substructureLoading = true;
    });
    const downloaded = this.downloadedResults.get("compound") || [];
    const smilesList = downloaded.map(item => item.entity?.smiles).filter(Boolean);
    try {
      const matchIndices = await fetchSubstructureMatches(query, smilesList, this.userStore.authData);
      const matchingSmiles = new Set(matchIndices.map(i => smilesList[i]));
      runInAction(() => {
        this._substructureMatches = matchingSmiles;
        this._substructureLoading = false;
        this.updateCategoryResults(true);
      });
    } catch (e) {
      console.error("Substructure filter failed", e);
      runInAction(() => {
        this._substructureQuery = null;
        this._substructureMatches = null;
        this._substructureLoading = false;
      });
      toast.error("Substructure filter failed");
    }
  };

  clearSubstructureFilter = () => {
    this._substructureQuery = null;
    this._substructureMatches = null;
    this._substructureLoading = false;
    this.updateCategoryResults(true);
  };

  get chatContext() {
    return this._chatContext;
  }

  set chatContext(q) {
    this._chatContext = q;
  }

  get firstNonemptyCategory() {
    if (this.categoryTotals.size === 0) {
      return this.currentCategory || DEFAULT_CATEGORY_KEY;
    }
    for (let tab of this.tabs) {
      // Skip graph tab - it's not a valid automatic switch target
      if (tab.key !== "graph" && this.categoryTotals.get(tab.key) !== 0) {
        return tab.key;
      }
    }
    return this.queryCategory;
  }

  get hasNonQueryCategoryResults() {
    if (this.categoryTotals.size === 0) {
      return false;
    }
    const queryCategory = this.queryCategory;
    for (let [category, total] of this.categoryTotals.entries()) {
      if (category !== queryCategory && total > 0) {
        return true;
      }
    }
    return false;
  }

  get graphTargetCategory() {
    return this._graphTargetCategory;
  }

  set graphTargetCategory(cat) {
    this._graphTargetCategory = cat;
  }

  get currentCategory() {
    return this._currentCategory;
  }

  set currentCategory(key) {
    let filterChanged = false;
    if (this.currentCategory !== key) {
      const lastCategory = this.currentCategory;
      this._currentCategory = key
      console.debug(`[currentCategory setter] Changed from "${lastCategory}" to "${key}"`);
      Analytics.event(Analytics.EVENTS.TAB_SELECT, { category: key });
      this.restoreBlankedSearchTerms();
      // Re-apply filter (or clear it), if any
      if (CLEAR_FILTER_ON_TAB_CHANGE) {
        filterChanged = true;
        this.filterString = "";
      }
      if (key === "graph") {
        if (lastCategory !== this.queryCategory) {
          this.graphTargetCategory = lastCategory;
        }
      }
      else {
    this.updateCategoryResults(filterChanged, true);
      }
    }
  }

  get downloadedResults() {
    return this._downloadedResults;
  }

  getEntityById = (id) => {
    return toJS(this.entities[id]);
  }

  get entities() {
    return this._entities;
  }

  set entities(e) {
    this._entities = e;
  }

  get authData() {
    return this.userStore.authData;
  }

  filterByDirectConnection = directConnectionID => {
    this.runNewSearch({...this.params, tab: this.currentCategory, directConnectionID, queryEntities: this.queryEntities}, {retainResultsLayout: true});
  };

  filterByCommunity = neighborhood => {
    this.runNewSearch({...this.params, tab: this.currentCategory, neighborhood, queryEntities: this.queryEntities}, {retainResultsLayout: true});
  };

  // Are there more results to be obtained?
  get isLoading() {
    return this.awaitingInitialResults || this.isFetchingMore;
  }

  // Are there more results to be obtained for the current category?
  get isCategoryLoading() {
    const downloaded = this.downloadedResults.get(this.currentCategory)?.length || 0;
    const total = this.categoryTotals.get(this.currentCategory);
    // Treat unknown total (totals not yet received) as still loading
    return this.isLoading && (total === undefined || downloaded < total);
  }

  get hasFullResults() {
    const result = this.searchIDs?.length && this.queryEntities?.length && !this.isLoading;
    if (result) {
      console.debug('[SearchSessionStore.hasFullResults] TRUE', {
        searchIDsLength: this.searchIDs?.length,
        queryEntitiesLength: this.queryEntities?.length,
        isLoading: this.isLoading
      });
    }
    return result;
  }

  // True when a search is in flight with searchIDs already assigned — the exact condition
  // under which cacheCurrentResults() is a no-op (see hasFullResults/cacheCurrentResults
  // above), i.e. navigating away right now would silently lose live/uncached state. See
  // PLEX-6054 — used by App.js/NavBar.js to decide when to warn before navigation.
  get hasActiveUncachedSearch() {
    return Boolean(this.isLoading && this.searchIDs?.length);
  }

  stringify = item => {
    return JSON.stringify(item);
  }

  _clipboardText = (category, filtered = false, idsOnly = false) => {
    // Mimic what's included in the CSV download
    const downloaded = (filtered ? this.categoryResults : this.downloadedResults.get(category)) || [];
    return downloaded.reduce((result, {entity, rank}) => {
      return idsOnly ? `${result}\n${entity.id}` : `${result}\n${rank.toFixed(4)}\t${entity.id}\t${getBestName(entity)}`;
    }, "");
  }

  get clipboardText() {
    if (this.isLoading) return "";
    return Array.from(this.downloadedResults.keys()).reduce((result, category) => {
      if (!this.categories[category]) {
        return result;
      }
      return `${result}\n${this.categories[category].name}\n${this._clipboardText(category)}`;
    }, "");
  }

  get categoryClipboardText() {
    return this.isLoading ? "" : this._clipboardText(this.currentCategory, true);
  }

  // Total number of available results for the current category
  get categoryResultsTotal() {
    return this.categoryTotals.get(this.currentCategory) || null;
  }

  get categoryTotals() {
    return this._categoryTotals;
  }

  set categoryTotals(totals) {
    this._categoryTotals = totals;
  }

  // Currently available current category results (filtered)
  get categoryResults() {
    // TODO: toJS() on this?
    return this._categoryResults;
  };

  set categoryResults(results) {
    this._categoryResults = results;
  }

  get categoryResultsLength() {
    return this._categoryResults.length;
  }

  nearestCache = str => {
    if (str.length >= MIN_FILTER_LENGTH) {
      // Put a cap on how long a substring we'll look at
      const lc = str.toLowerCase().substring(0, 16);
      return (this._filterCache.has(lc) && this._filterCache.get(lc))
        || this.nearestCache(lc.substring(0, lc.length - 1));
    }
    return null;
  };

  fetchMembers = async(id, dsConfig = null, auth = null, fetchParams = {}) => {
    if (this.templates === null) {
      await this.fetchDsConfigMetadata(this.dsConfig);
    }
    const members = await fetchMembers(ids, dsConfig || this.dsConfig, auth || this.authData, fetchParams);
    return members?.map(entity => this.patchEntity(entity)) || [];
  }

  fetchEntities = async (ids = [], dsConfig = null, auth = null, fetchParams = {}) => {
    if (ids.length === 0) {
      return [];
    }
    // Returns a list of entities matching the given IDs
    // With partial=true, backend returns array with null for invalid IDs at their positions
    if (this.templates === null) {
      await this.fetchDsConfigMetadata(this.dsConfig);
    }
    const response = await fetchEntities(ids, dsConfig || this.dsConfig, auth || this.authData, fetchParams);
    // Map entities, patching valid ones and preserving nulls for invalid IDs.
    return (response.entities || []).map((entity) => {
      if (entity === null) return null;
      return this.patchEntity(entity);
    });
  };

  patchEntity = (e) =>
    patchEntity(e, this.categories, this.datasets[e.dataset],
                this.templates, this.preferredNames,
                () => this.datasets);

  // Resolve multiple terms, on init or paste
  fetchSuggestions = async (terms, {category = null, compact = false, taxID = null}, auth = null) => {
    // Ensure metadata is loaded before attempting category matching
    if (this.templates === null) {
      await this.fetchDsConfigMetadata(this.dsConfig);
    }
    const {organismRestriction} = this.appStatusStore;
    const {dsConfig} = this.queuedSearchParams;
    if (taxID === null && organismRestriction !== TAXID_NONE) {
      taxID = organismRestriction;
    }
    const split = typeof(terms) === "string" ? terms.split(" ") : [];
    if (category === null && split.length === 2) {
      const prefix = split[1].toLowerCase();
      let count = 0;
      if (prefix.length > 2) {
        category = Object.entries(this.categories)
          .reduce((result, [key, info]) => {
          if (key.startsWith(prefix) || info.name.toLowerCase().startsWith(prefix)) {
            count += 1;
            return key;
          }
          return result;
        }, null);
        if (category != null) {
          terms = split[0];
          if (count > 1) {
            category = null;
          }
        }
      }
    }
    const q = (typeof(terms) === "string" ? terms : terms.join("\n")).trim();
    const labels = [];
    if (terms.map) {
      terms = terms.map(term => {
        const [unlabeled, label] = extractLabel(term);
        labels.push(label);
        return unlabeled;
      });
    }
    else {
      const [unlabeled, label] = extractLabel(terms);
      labels.push(label);
      terms = unlabeled;
    }
    const _t0 = Date.now();
    return fetchSuggestions(terms, {category, taxID, compact, dsConfig}, auth || this.authData)
      .then(suggestions => {
        Analytics.event(Analytics.EVENTS.AUTOSUGGEST, { query: q.slice(0, 100), elapsed: Date.now() - _t0 });
        suggestions.forEach((el, idx) => {
          if (el.entity) {
            // Clears any extant labels if no label was provided
            saveCustomLabel(el.id, idx < labels.length ? labels[idx] : null);
            el.entity = this.patchEntity(el.entity);
            el.entity['match-type'] = el['match-type'];
            el.entity['match-field'] = el['match-field'];
            el.entity['match-term'] = el['match-term'];
            el.entity['match-value'] = el['match-value'];
            el.entity['match-text'] = el['match-text'];
            el.entity['match-score'] = el['match-score'];
            el.entity['_sort_score'] = el['_sort_score'];
            if (DEV && el.entity.label && el['match-score'] !== undefined) {
              el.entity.label = `${el.entity.label} <${el['match-score']}>`;
            }
          }
        });
        // Ensure results are returned in the same order as the input terms
        if (typeof(terms) === "string" || terms.length === 1) {
          return suggestions;
        }
        const emap = suggestions.reduce((result, el) => {
          result[el['match-term']] = el;
          return result;
        }, {});
        return terms.map(term => emap[term]);
      });
  };

  _debouncedSetFilterString = _.debounce((input) => {
    const lower = input.toLowerCase();
    if (lower !== this.filterString) {
      runInAction(() => {
        this._filterString = lower;
        this.updateCategoryResults(true);
      });
    }
  }, 200);

  set filterString(input) {
    this._debouncedSetFilterString(input);
  }

  // Update current category results after a filter or data change
  updateCategoryResults = (filterChanged, dataChanged) => {

    const downloaded = this.downloadedResults.get(this.currentCategory) || [];
    let categoryResults = downloaded;
    let filterMatched = true;
    if (dataChanged) {
      this._filterCache.clear();
    }
    if (this.isFiltering) {
      if (!dataChanged && !filterChanged) {
        console.debug("updateCategoryResults: ignore update");
        return;
      }
      const filterString = this.filterString.trimEnd();
      const unfiltered = this.nearestCache(filterString) || downloaded;
      const pattern = looseMatchRegExp(filterString, "i");
      const filtered = unfiltered.filter(filterPredicate(pattern));
      const terms = filterString.startsWith(" ")
        ? [this.filterString] : filterString.split(/\s+/).filter(x => x.length > 0);
      const fullMatches = new Set(filtered.map(item => item.id));
      // If multiple terms are provided, find additional results containing ALL terms
      if (terms.length > 1) {
        const discontiguousMatches = unfiltered.filter(item => !fullMatches.has(item.id))
          .reduce((result, item) => {
          if (!fullMatches.has(item.id)
            && _.every(terms, x => filterPredicate(looseMatchRegExp(x))(item))) {
            result.push(item);
          }
          return result;
        }, []);
        filtered.push(...discontiguousMatches);
        filtered.sort((a, b) => b.rank - a.rank);
      }

      this._filterCache.set(filterString, filtered);
      if (filtered.length > 0) {
        categoryResults = filtered;
        filterMatched = true;
      } else if (downloaded.length !== 0) {
        categoryResults = downloaded;
        filterMatched = false;
      }
    }
    else {
      //console.debug("Use unfiltered category results")
    }
    // Apply substructure filter (compounds only)
    if (this.isSubstructureFiltering && this.currentCategory === "compound") {
      categoryResults = categoryResults.filter(
        item => item.entity?.smiles && this._substructureMatches.has(item.entity.smiles)
      );
    }
    // Always assign a new array reference so MobX detects the change even when the
    // underlying downloadedResults array was mutated in-place (e.g. after activity splice).
    const next = categoryResults === downloaded ? categoryResults.slice() : categoryResults;
    this.categoryResults = next;
    this.isFilterMatched = filterMatched;
  };

  get filterString() {
    return this._filterString;
  };

  get clientSearchKey() {
    return this._clientSearchKey;
  }

  set clientSearchKey(key) {
    this._clientSearchKey = key;
  }

  isNewLocation = (location) => {
    const urlParams = this.canonicalParams(this.fromLocation(location));
    const currentParams = this.params;
    return !_.isEqual(urlParams, currentParams);
  }

  get clientDefaultParams() {
    return this._clientDefaultParams;
  }

  set clientDefaultParams(params) {
    this._clientDefaultParams = params;
  }

  runNewSearch = (newParams = null, {retainResultsLayout = false, conversationId = null, queryEntities: preResolvedEntities = null} = {}) => {
    if (newParams === null) {
      newParams = this.queuedSearchParams;
    }
    const {location: fromURL = null} = newParams;
    if (fromURL) {
      // Load params from URL
      newParams = this.fromLocation(fromURL);
      this.clientDefaultParams = {}
      // Merge in pre-resolved entities if provided, so we skip the async entity fetch
      if (preResolvedEntities?.length) {
        newParams.queryEntities = preResolvedEntities;
      }
    }
    else {
      if (newParams.queryEntities) {
        newParams.searchIDs = newParams.queryEntities.map(el => el.id).sort();
      }
      else {
        newParams.queryEntities = [];
      }
      newParams = {...this.queuedSearchParams, ...newParams};
    }
    if (this.searchIDs?.length && !_.isEqual(new Set(this.searchIDs), new Set(newParams.searchIDs))) {
      delete newParams.directConnectionID;
      delete newParams.neighborhood;
    }
    // Extract tab before cancelSearch() since cancelSearch() resets currentCategory
    const tabFromUrl = newParams.tab;
    delete newParams.tab;
    // Discard current results or anything currently running
    this.cancelSearch();
    // Restore ID only when loading a search that has an attached conversation,
    // so analyst turns remain associated with the right history item.
    if (conversationId) {
      this.currentSearchConversationId = conversationId;
    }
    // Now restore the tab from URL (after cancelSearch reset currentCategory to default)
    if (tabFromUrl) {
      this.currentCategory = tabFromUrl;
    }
    const {searchIDs = [], queryEntities = []} = newParams;
    if (searchIDs.length === 0) {
      if (queryEntities.length === 0) {
        throw new SearchError("No search IDs provided");
      }
      newParams.searchIDs = queryEntities.map(e => e.id).sort();
    }
    if (newParams.q) {
      this.chatContext = newParams.q;
    }
    if (newParams.gtc) {
      // Transient setting, used only for initialization from URLs
      this.graphTargetCategory = newParams.gtc;
      delete newParams.gtc;
    }

    // Update params before performing any asynchronous actions to ensure searchURL returns
    // the proper values
    this.params = this.canonicalParams(newParams);
    if (retainResultsLayout) {
      this.isFetchingMore = true;
      this.retainResultsLayout = true;
    } else {
      this.awaitingInitialResults = true;
    }

    // When entities aren't pre-resolved (URL init or reload), set resolvingTerms now so
    // the search box shows a resolving placeholder during the async entity fetch.
    if (queryEntities.length === 0 && newParams.searchIDs?.length) {
      this.resolvingTerms = newParams.searchIDs;
    }

    // History save is handled inside _runNewSearch after queryEntities are resolved.

    this._runNewSearch(newParams, fromURL)
      .catch(error => {
        if (error instanceof SearchCanceled) {
          return;
        }
        if (error instanceof NotAuthorized) {
          if (error.networkBlocked) {
            toast.error(error.message, {toastId: error.message});
            return;
          }
          console.debug("Unable to restore session information during search");
          return;
        }
        console.error(`Search failed`, error);
        if (error instanceof CORSError) {
          this.appStatusStore.corsError = true;
        } else if (error instanceof ServerOfflineError) {
          this.appStatusStore.serverOffline = true;
        }
        const errorMessage =
          error instanceof CORSError
          ? "CORS error: Check server configuration\nRequest blocked by CORS policy"
          : error instanceof ServerOfflineError
          ? "The Plex Search service is unavailable\nPlease try again later"
          : error instanceof ServerError
            ? error.message
            : error.status
              ? error.statusText ? `${error.statusText} (${error.status})`
                                 : `Unexpected error (${error.status})`
              : `${error}`;
        this.clear(errorMessage);
        toast.error(errorMessage, {toastId: `${errorMessage}`});
      });
  }

  get searchURL() {
    return SearchContext.getAppSearchURL(this.params, this.localParams, {withLabels: true});
  }

  get queuedSearchURL() {
    // Exclude history_id from queued URL — it belongs to the current search, not the new one
    const { history_id: _, ...localParamsWithoutHistory } = this.localParams;
    return SearchContext.getAppSearchURL(this.queuedSearchParams, localParamsWithoutHistory, {withLabels: true});
  }

  get localParams() {
    const localParams = this.currentCategory ? {tab: this.currentCategory} : {};
    if (this.isFiltering) {
      localParams.filter = this.filterString;
    }
    if (this.chatContext) {
      localParams.q = this.chatContext;
    }
    if (this.graphTargetCategory) {
      localParams.gtc = this.graphTargetCategory;
    }
    if (this.currentSearchConversationId) {
      localParams.history_id = this.currentSearchConversationId;
    }
    return localParams;
  }

  /**
   * Saves the current search to history
   *
   * @param {boolean} immediate - If true, save immediately even if results aren't complete yet
   *                              Used for initial save on search submit to ensure item appears
   *                              in history even if navigation/errors occur
   * @param {HistoryStore} historyStore - Optional historyStore instance (avoids circular dependency)
   */
  saveSearchToHistory = async (immediate = false, historyStore = null) => {
    console.debug('[SearchSessionStore.saveSearchToHistory] Called', {
      immediate,
      hasSearchIDs: !!this.searchIDs?.length,
      hasFullResults: this.hasFullResults,
      searchIDsLength: this.searchIDs?.length,
      queryEntitiesLength: this.queryEntities?.length,
      isLoading: this.isLoading
    });

    // For immediate saves (on search submit), just check we have search IDs and query entities
    // For regular saves (after results), also require hasFullResults
    const canSave = immediate
      ? (this.searchIDs?.length && this.queryEntities?.length)
      : (this.searchIDs?.length && this.hasFullResults);

    if (!canSave) {
      console.debug('[SearchSessionStore.saveSearchToHistory] Skipping save - conditions not met');
      return;
    }

    if (this._savingToHistory) {
      console.debug('[SearchSessionStore.saveSearchToHistory] Save already in flight, skipping');
      return;
    }
    this._savingToHistory = true;

    try {
      // Compute canonical URL without history_id (history_id is a local param, not part of the search identity)
      const rawUrl = this.searchURL;
      let url = rawUrl;
      if (rawUrl) {
        try {
          const urlObj = new URL(rawUrl, window.location.origin);
          const hashParams = new URLSearchParams(urlObj.hash ? urlObj.hash.slice(1) : '');
          hashParams.delete('history_id');
          const cleanHash = hashParams.toString() ? `#${hashParams.toString()}` : '';
          url = `${urlObj.pathname}${urlObj.search}${cleanHash}`;
        } catch (e) {
          // Leave url as-is if parsing fails
        }
      }

      // Generate a title from query entities
      const { directConnectionID, neighborhood } = this.params;
      const filterPrefix = directConnectionID
        ? `[DC ${getBestName(this.entities[directConnectionID], true) || directConnectionID}] `
        : (neighborhood != null ? `[NBR ${neighborhood}] ` : "");
      const title = this.queryEntities.length > 0
        ? `${filterPrefix}${this.queryEntities.map(qe => getBestName(qe, true)).join(", ")}`
        : "Unnamed Search";

      // Calculate total result count across all categories
      const totalResults = Array.from(this.categoryTotals.values()).reduce((sum, count) => sum + count, 0);

      if (!historyStore) {
        historyStore = this.historyStore;
      }

      // Check if an existing non-conversation search already has this canonical URL.
      // This prevents duplicate entries when the user refreshes or re-runs the same search.
      if (historyStore?.searches) {
        const existing = historyStore.searches.find(s => s.context?.url === url && !(s.metadata?.turnCount > 0));
        if (existing) {
          console.debug('[SearchSessionStore.saveSearchToHistory] Found existing search by canonical URL, reusing ID', existing.id);
          this.currentSearchConversationId = existing.id;
          this._savingToHistory = false;
          return;
        }
      }

      // Derive a stable ID from the canonical URL so the same search always
      // produces the same UUID — enabling server-side idempotency checks.
      const clientId = searchUUIDFromURL(url);
      this.currentSearchConversationId = clientId;

      const searchConversationData = {
        id: clientId, // URL-derived stable UUID as idempotency key
        context: {
          title,
          url, // This marks it as a search-conversation and serves as the natural key
          queryEntities: this.queryEntities.map(qe => ({
            id: qe.id,
            name: getBestName(qe, true),
            category: qe.category
          })),
          // Store params as JSON string to avoid DynamoDB float serialization issues
          paramsJSON: JSON.stringify(this.params),
          categoryTotals: Object.fromEntries(this.categoryTotals.entries()),
          totalResults,
          dsConfig: this.dsConfig,
        },
        turns: [], // Empty turns initially - analyst will add turns when used
      };

      // Save to cloud via HistoryStore
      console.debug('[SearchSessionStore.saveSearchToHistory] Preparing to save search-conversation', {
        immediate,
        id: clientId,
        url: url,
        title: searchConversationData.context.title,
        hasTurns: searchConversationData.turns.length > 0,
        isOnline: historyStore?.isOnline,
        isAuthenticated: historyStore?.userStore?.isAuthenticated
      });

      if (historyStore?.isOnline && historyStore?.userStore?.isAuthenticated) {
        // Save as a search-conversation (SEARCH type in backend)
        // Backend uses context.url to determine it's a SEARCH not CONVERSATION
        historyStore.saveSearch(searchConversationData)
          .then(result => {
            console.debug('[SearchSessionStore.saveSearchToHistory] Save succeeded', {
              id: result.id,
              url: searchConversationData.context.url
            });
            // Update with server-confirmed ID (may differ from clientId if server generated its own)
            this.currentSearchConversationId = result.id;
          })
          .catch(error => {
            console.error('Failed to save search-conversation to cloud:', error);
            // Clear the optimistic ID so a retry is possible
            if (this.currentSearchConversationId === clientId) {
              this.currentSearchConversationId = null;
            }
          })
          .finally(() => {
            this._savingToHistory = false;
          });
      } else {
        console.debug('[SearchSessionStore.saveSearchToHistory] Skipping save - offline or not authenticated');
        // Clear optimistic ID since we didn't actually save
        if (this.currentSearchConversationId === clientId) {
          this.currentSearchConversationId = null;
        }
        this._savingToHistory = false;
      }
    } catch (e) {
      console.error("Failed to save search to history", e);
      this._savingToHistory = false;
    }
  }

  fromLocation = (location) => {
    try {
      const params = SearchContext.parseQueryStringParams(location.search, location.hash);
      return {...this.defaultSearchParams, ...params, location};
    }
    catch(error) {
      this.lastError = `Invalid URL: ${error}`;
      return {...this.defaultSearchParams};
    }
  }

  get queryCategory() {
    return this.queryEntities?.length > 0 ? this.queryEntities[0].baseCategory : null;
  }

  searchSimKey = (params) => {
    // If it's already set to non-default, leave it alone
    if (params.simType || params.simType === "") {
      return params.simType;
    }
    const {queryEntities: qe = []} = params;
    if (qe?.length === 0) {
      return params.simType;
    }
    const {searchIDs: ids = qe.map(el => el.id)} = params;
    if (ids.length === 0) {
      return params.simType;
    }
    const searchCategory = qe?.length > 0 ? qe[0].baseCategory : null;
    if (searchCategory === "compound") {
      return DEFAULT_COMPOUND_SIMTYPE;
    }
    if (new Set(["cpdset", "geneset"]).has(searchCategory)) {
      //return "set";
    }
    if (searchCategory === "org") {
      return "org";
    }
    return null;
  }

  canonicalParams = (params, includeQueryEntities = false) => {
    // Remove all parameters whose value matches the server default
    // or those that should not be passed to the server
    // Avoid modifying the original object
    params = {...this.defaultSearchParams, ...params};
    if (params.queryEntities?.length) {
      params.searchIDs = Array.from(new Set(params.queryEntities.map(x => x.id))).sort();
    }
    else {
      params.searchIDs = Array.from(new Set(params.searchIDs)).sort();
    }

    params = this.normalizeSimilarityParams(params);
    return Object.entries(params)
      .filter(([key, value]) => {
        if (value === undefined) return false;
        const def = this.defaultSearchParams[key];
        // Treat null as equivalent to undefined (absent) when the default is also absent
        if (value === null && def === undefined) return false;
        return !isEqual(value, def);
      })
      //.filter(([key, value]) => value || !!value !== !!this.defaultSearchParams[key])
      .filter(([key, value]) => !LOCAL_PARAMS.has(key)
                                && (includeQueryEntities || key !== 'queryEntities'))
      .reduce((obj, [key, value]) => {
        if (key === "searchIDs" && value && value.length === 0) {
          return obj;
        }
        obj[key] = value;
        return obj;
      }, {});
  }

  _runNewSearch = async (nextParams, fromURL = false) => {
    // Ensure nocache is passed only once, and not preserved as a parameter
    const nocache = nextParams.nocache;
    delete nextParams.nocache;
    // Save queryEntities before canonicalParams strips them (LOCAL_PARAMS excludes queryEntities)
    const passedQueryEntities = nextParams.queryEntities || [];
    nextParams = this.canonicalParams({...nextParams});

    const searchConversationId = this.currentSearchConversationId
      || searchUUIDFromURL(SearchContext.getAppSearchURL(nextParams, {}, {withLabels: true}));
    Analytics.event(Analytics.EVENTS.SEARCH, {
      query: (nextParams.q || "").slice(0, 100),
      conversation_id: searchConversationId,
      conversation_link: `${window.location.origin}/#history_id=${searchConversationId}`,
    });

    // Deduplicate concurrent calls before any async work: use search IDs + params as a content key.
    // createSearchKey uses a timestamp so it can't be used here; instead compare serialized params.
    const pendingKey = JSON.stringify(nextParams);
    if (this._pendingSearchKey === pendingKey) {
      console.debug("Search already pending, ignoring duplicate call");
      return;
    }
    this._pendingSearchKey = pendingKey;

    const queryEntities = passedQueryEntities.length > 0 ? passedQueryEntities : (nextParams.queryEntities || []);
    if (queryEntities.length === 0) {
      runInAction(() => this.resolvingTerms = nextParams.searchIDs);
      try {
        const resolved = await this.fetchEntities(nextParams.searchIDs);
        runInAction(() => this.resolvingTerms = []);
        queryEntities.push(...resolved.filter(el => el !== null));
        if (queryEntities.length === 0) {
          // All IDs failed to resolve (backend returned nulls, not an exception).
          // Use the original searchIDs in the message since queryEntities is empty.
          const msg = `Unrecognized query IDs: "${nextParams.searchIDs?.join('", "')}"`;
          this._pendingSearchKey = null;
          this.clear(msg);
          return;
        }
        nextParams.searchIDs = queryEntities.map(el => el.id);
      }
      catch(error) {
        if (error instanceof ServerOfflineError || error instanceof ServerError || error instanceof NotAuthorized) {
          throw error; // Let the outer catch handler deal with it
        }
        captureMessage("_runNewSearch: unrecognized IDs", {
          level: "error",
          fingerprint: ["search-unrecognized-ids"],
          data: error
        });
        const msg = `Unrecognized query IDs: "${(error.data || []).join('", "')}"`;
        this._pendingSearchKey = null;
        this.clear(msg);
        return;
      }
    }
    if (nextParams.dsConfig !== this.dsConfig || this.dsConfig === null) {
      this.fetchDsConfigMetadata(nextParams.dsConfig).then(() => {});
    }
    // Pre-fetch the directConnectionID entity so FiltersBox can show its label
    // immediately rather than falling back to the raw plex ID.
    if (nextParams.directConnectionID && !this.entities[nextParams.directConnectionID]) {
      this.fetchEntities([nextParams.directConnectionID]).then(results => {
        const entity = results[0];
        if (entity) {
          runInAction(() => { this.entities[entity.id] = entity; });
        }
      }).catch(() => {});
    }
    const params = this.canonicalParams(nextParams);
    const key = this.createSearchKey(params);
    if (key === this.clientSearchKey) {
      console.debug("Search already in progress");
      this._pendingSearchKey = null;
      return;
    }
    // Search is now committing - clear the pending key so future searches aren't blocked
    this._pendingSearchKey = null;
    runInAction(() => {
      // Update params again, in case we're initializing from a URL, in which case the defaultParams may not have
      // been loaded before canonicalParams was applied
      this.params = params;
      this.queuedSearchParams = {};
      this.queryEntities = queryEntities;
      this.isFetchingMore = true;
      this.clientSearchKey = key;
      console.debug(`New search URL ${this.searchURL}`);
    });
    // Save to history now that queryEntities are resolved. Skip if App.js pre-set a
    // conversation ID (meaning it found an existing history item for this search URL).
    if (!this.currentSearchConversationId) {
      setTimeout(() => this.saveSearchToHistory(true), 0);
    }
    if (queryEntities.length < nextParams.searchIDs.length) {
      toast.warning(`Not all provided query IDs were resolved`);
    }
    else if (queryEntities.some(el => el.baseCategory !== queryEntities[0].baseCategory)) {
      const cats = new Set(queryEntities.map(el => el.baseCategory));
      const error = `All query IDs must share the same category (you included ${Array.from(cats).join(", ")}).
      Remove query items to ensure you're including a single category.`;
      this.clear(error);
      return;
    }
    return this.fetchSearchResults(key, {...params, nocache})
      .finally(() => {
        if (key === this.clientSearchKey) {
          runInAction(() => this.isFetchingMore = false);
        }
      });
  }

  exportSearchResultsAsFormat = async (format) => {
    const exportConversationId = this.currentSearchConversationId
      || searchUUIDFromURL(SearchContext.getAppSearchURL(this.params, {}, {withLabels: true}));
    Analytics.event(Analytics.EVENTS.EXPORT, {
      format,
      conversation_id: exportConversationId,
      conversation_link: `${window.location.origin}/#history_id=${exportConversationId}`,
      result_count: this.categoryTotals.get(this.currentCategory) || 0,
    });
    return exportSearchResults({...this.params, full: true}, this.authData, format);
  };

  exportEvidenceChains = async (targetID) => {
    const searchParams = { ...this.params, targetID: targetID, exportFormat: "xlsx"};
    return exportSearchResults(searchParams, this.authData, "xlsx");
  };

  // paramsOverride (optional) — a one-shot params object used instead of
  // this.params for this fetch only (PLEX-4228). EChainStore passes the
  // anchor searchIDs this way so callers no longer mutate this.params in
  // place. Default null preserves the existing behavior.
  // signal (optional) — an AbortSignal threaded down to the underlying
  // fetch so EChainStore can truly cancel an in-flight request (PLEX-4234).
  fetchEvidenceChains = async (targetID, datasetsFilter = [], onProgress = null, paramsOverride = null, signal = null) => {
    const results = await fetchEChains(
      paramsOverride || this.params,
      targetID,
      datasetsFilter,
      this.authData,
      {
        useStreaming: false,  // Backend doesn't support streaming for echains
        signal,               // AbortSignal → underlying fetch (PLEX-4234)
        onProgress: onProgress || ((progress, total, info) => {
          runInAction(() => {
            this.searchProgress = `${progress}/${total} ${info}`;
          });
        })
      }
    );

    // Patch all entities in the evidence chain paths
    if (results.paths) {
      results.paths = results.paths.map(path =>
        path.map(el => {
          if (el.entity) {
            el.entity = this.patchEntity(el.entity);
          }
          return el;
        })
      );
    }

    return results;
  };

  fetchMoreResults = async (
      clientSearchKey,
      pageToken = null,
  ) => {
    if (clientSearchKey !== this.clientSearchKey) {
      console.debug(`Discard obsolete results ${clientSearchKey} (${this._clientSearchKey})`)
      return;
    }
    let response;
    try {
      response = await fetchSearch(
        {
          ...this.params,
          full: true,
          pageToken: pageToken
        },
        this.authData,
        {searchType: "search", isCanceled: () => clientSearchKey !== this.clientSearchKey},
      );
    } catch (err) {
      if (err?.status === 400) {
        console.debug('[fetchMoreResults] Search session expired on server, stopping pagination');
        return;
      }
      throw err;
    }
    const {pageToken: nextPageToken, results, totals = {}, stream_id: streamId, search_id: searchId} = response;

    // Capture server-side search ID for activity API (first page only)
    if (searchId && !this._serverSearchId) {
      runInAction(() => { this._serverSearchId = searchId; });
    }

    // If server returns a stream_id during pagination, it means the search was restarted
    // (e.g., server restart or cache clear). Switch to streaming mode.
    if (streamId) {
      console.debug(`Search restarted during pagination, switching to streaming with ID: ${streamId}`);
      runInAction(() => {
        this.searchStreamId = streamId;
      });
      return;
    }

    if (results) {
      this.handleIncrementalSearchResults(results, totals, nextPageToken);
      if (!!nextPageToken && !this.downloadLimitReached) {
        return this.fetchMoreResults(clientSearchKey, nextPageToken);
      }
      // Check for categories with totals > 0 but 0 downloaded results (streaming batch bug workaround)
      await this.fetchMissingCategoryResults(clientSearchKey);
    }
    else {
      throw new Error(`Incremental search results missing from response (${JSON.stringify(response)}`);
    }
  };

  /**
   * Workaround for server streaming bug: check for categories that have totals > 0
   * but 0 downloaded results after streaming/pagination completes, and fetch them directly.
   */
  fetchMissingCategoryResults = async (clientSearchKey) => {
    if (clientSearchKey !== this.clientSearchKey) {
      return;
    }
    const missingCategories = [];
    for (const [category, total] of this.categoryTotals.entries()) {
      const downloaded = this.downloadedResults.get(category)?.length || 0;
      if (total > 0 && downloaded < total) {
        missingCategories.push(category);
      }
    }
    if (missingCategories.length === 0) {
      return;
    }
    console.debug(`[fetchMissingCategoryResults] Fetching missing results for categories: ${missingCategories.join(', ')}`);
    // Fetch without streaming to get results directly
    const response = await fetchSearch(
      {
        ...this.params,
        full: true,
        pageToken: null,
      },
      this.authData,
      {searchType: "search", isCanceled: () => clientSearchKey !== this.clientSearchKey, useStreaming: false},
    );
    const {results} = response;
    if (results && clientSearchKey === this.clientSearchKey) {
      // Only process the missing categories
      const missingResults = {};
      for (const cat of missingCategories) {
        if (results[cat]?.length > 0) {
          missingResults[cat] = results[cat];
        }
      }
      if (Object.keys(missingResults).length > 0) {
        console.debug(`[fetchMissingCategoryResults] Retrieved results for: ${Object.entries(missingResults).map(([k, v]) => `${k}(${v.length})`).join(', ')}`);
        this.handleIncrementalSearchResults(missingResults, null, null);
      }
    }
  };

  handleIncrementalSearchResults = (batch, totals = null, pageToken = null) => {
    console.debug(`[handleIncrementalSearchResults] dl.size=${this.downloadedResults.size} batch categories:`, Object.keys(batch).map(cat => `${cat}(${batch[cat].length})`).join(', '));
    if (totals) {
      this.categoryTotals = new Map(Object.entries(totals || {}));
      // Auto-switch to first nonempty category if current category has no results
      const currentCategoryTotal = this.categoryTotals.get(this.currentCategory) || 0;
      if (currentCategoryTotal === 0 && this.currentCategory !== "graph") {
        const firstNonempty = this.firstNonemptyCategory;
        if (firstNonempty && firstNonempty !== this.currentCategory) {
          console.debug(`[handleIncrementalSearchResults] Auto-switching from empty category "${this.currentCategory}" to "${firstNonempty}"`);
          this.currentCategory = firstNonempty;
        }
      }
    }
    const dsConfig = this.dsConfig;
    const dl = this.downloadedResults;
    let currentCategoryUpdated = false;
    if (dl.size === 0) {
      console.debug(`[handleIncrementalSearchResults] FIRST BATCH - initializing downloadedResults`);
      Object.entries(batch).forEach(([cat, results]) => {
        console.debug(`[handleIncrementalSearchResults] FIRST BATCH ${cat}: ${results.length} items, first ordinals will be 1-${results.length}`);
        dl.set(cat, observable(results.map((el, idx) => {
          el.ordinal = idx + 1;
          el.dsConfig = dsConfig;
          el.entity = this.patchEntity(el.entity);
          this.entities[el.entity.id] = el.entity;
          return el;
        }), {deep: false}));
        if (cat === this.currentCategory) {
          this.updateCategoryResults(false, true);
          currentCategoryUpdated = true;
        }
        if (this.isSubstructureFiltering && cat === "compound") {
          this.setSubstructureFilter(this._substructureQuery);
        }
      });
    } else {
      Object.entries(batch).forEach(([cat, categoryResult]) => {
        if (categoryResult.length !== 0) {
          const downloaded = dl.get(cat);
          const base_idx = downloaded.length;
          categoryResult.forEach((el, idx) => {
            el.ordinal = base_idx + idx + 1;
            el.dsConfig = dsConfig;
            el.entity = this.patchEntity(el.entity);
            this.entities[el.entity.id] = el.entity;
          });
          downloaded.push(...categoryResult);
          if (downloaded.length >= this.downloadLimit) {
            this.downloadLimitReached = true;
            this.categoryTotals.set(cat, downloaded.length);
          }
          if (cat === this.currentCategory) {
            this.updateCategoryResults(false, true);
            currentCategoryUpdated = true;
          }
          if (this.isSubstructureFiltering && cat === "compound") {
            this.setSubstructureFilter(this._substructureQuery);
          }
        }
      });
    }
    // Ensure current category results are updated even if no results for that category in this batch
    // This prevents showing stale results when streaming delivers categories incrementally
    if (!currentCategoryUpdated && this.currentCategory) {
      this.updateCategoryResults(false, true);
    }
    // Detect if activity was already computed server-side (e.g. on page reload).
    // Uses 5% threshold to distinguish full computation from sparse base-dataset activity.
    // entity.activity is an array post-patchEntity; check for non-empty array.
    if (!this._activityAvailable && batch["compound"]?.length >= 10) {
      const compounds = batch["compound"];
      const withActivity = compounds.filter(el => Array.isArray(el.entity?.activity) && el.entity.activity.length > 0).length;
      if (withActivity / compounds.length >= 0.05) {
        this._activityAvailable = true;
      }
    }
  };

  fetchGraphData = async (targetCategory, searchParams = null, options = {}) => {
    searchParams = (searchParams || this.params);
    this.graphTargetCategory = targetCategory;

    // Use retry-enabled fetchGraphData from search.js
    // This handles cache miss scenarios with exponential backoff
    return fetchGraphDataWithRetry(
      searchParams,
      targetCategory,
      this.authData,
      {
        isCanceled: options.isCanceled,
        onRetry: (attempt, delay, reason) => {
          runInAction(() => {
            this.searchProgress = `Retrying graph generation (attempt ${attempt}): ${reason}`;
          });
        },
        onProgress: (progress, total, info) => {
          runInAction(() => {
            this.searchProgress = `Generating graph: ${progress}/${total} ${info}`;
          });
        }
      }
    ).then(({graph}) => ({graph, targetCategory, queryCategory: this.queryCategory}));
  }

  fetchSearchResults = async (clientSearchKey, params) => {
    try {
      const response = await fetchSearch(
        {
          ...this.canonicalParams(params),
          full: true,
          pageToken: null,
        },
        this.authData,
        {
          searchType: "search",
          isCanceled: () => this.clientSearchKey !== clientSearchKey,
          useStreaming: true,
          onProgress: (progress, total, info) => {
            runInAction(() => {
              this.searchProgress = `${progress}/${total} ${info}`;
            });
          }
        },
      );

      const {stream_id: streamId, pageToken, results, totals = {}, error = null, search_id: searchId} = response;

      if (this.clientSearchKey !== clientSearchKey) {
        console.debug("Ignore stale search results");
        return;
      }

      // If we got a stream_id, store it for streaming
      if (streamId) {
        runInAction(() => {
          this.searchStreamId = streamId;
        });
        console.debug(`Search streaming started with stream ID: ${streamId}`);
        return;
      }

      // Legacy polling mode fallback (if streaming not available)
      if (results) {
        runInAction(() => {
          this.awaitingInitialResults = false;
          this.retainResultsLayout = false;
          if (searchId && !this._serverSearchId) {
            this._serverSearchId = searchId;
          }
          this.handleIncrementalSearchResults(results, totals, pageToken);
        });
        if (!!pageToken) {
          return this.fetchMoreResults(clientSearchKey, pageToken);
        }
      }
      else if (error) {
        throw new SearchError(error);
      }
      else {
        console.warn("No incremental search results in response", response);
      }
    } catch (error) {
      // Ignore errors from obsolete searches
      if (this.clientSearchKey === clientSearchKey) {
        throw error;
      }
    }
  };

  /**
   * Handle streaming search progress updates from SSE
   */
  handleStreamProgress = (progress, total, info) => {
    runInAction(() => {
      this.searchProgress = info ? `${progress}/${total} ${info}` : `${progress}/${total}`;
    });
  };

  /**
   * Handle streaming search results from SSE
   * This is called when the server sends the first batch of results via search_results event.
   * After receiving results, we MUST close the SSE stream and continue with pagination.
   */
  handleStreamResults = (data) => {
    const {results, totals, page_token: pageToken, search_id: searchId} = data;
    if (searchId && !this._serverSearchId) {
      this._serverSearchId = searchId;
    }

    if (results) {
      runInAction(() => {
        this.awaitingInitialResults = false;
        this.retainResultsLayout = false;
        this._streamRetryCount = 0;
        this.handleIncrementalSearchResults(results, totals, pageToken);
        // CRITICAL: Close the SSE stream immediately after receiving first batch
        // to prevent duplicate processing of results
        this.searchStreamId = null;
      });

      // If there's a page token, continue fetching more results via pagination
      if (pageToken && !this.downloadLimitReached) {
        this.fetchMoreResults(this.clientSearchKey, pageToken);
      } else {
        // No pagination needed - still check for missing category results
        this.fetchMissingCategoryResults(this.clientSearchKey);
      }
    }
  };

  /**
   * Handle streaming search errors from SSE
   */
  handleStreamError = (error) => {
    const MAX_STREAM_RETRIES = 3;
    const hasNoResults = this._downloadedResults.size === 0;

    // If results were already received, this is the natural SSE close event
    // (the browser fires onerror when the server closes the connection cleanly).
    // Ignore it — the stream completed successfully.
    if (!hasNoResults) {
      console.debug("[handleStreamError] Ignoring post-success SSE close event", error);
      return;
    }

    const canRetry = hasNoResults && this._streamRetryCount < MAX_STREAM_RETRIES;
    const clientSearchKey = this.clientSearchKey;

    console.debug("Search stream error:", error, {canRetry, retryCount: this._streamRetryCount, hasNoResults});

    if (canRetry) {
      runInAction(() => {
        this._streamRetryCount += 1;
        this.searchStreamId = null;
        this.searchProgress = `Reconnecting... (attempt ${this._streamRetryCount})`;
      });
      // Retry after a short delay — backend may still be starting up
      const delay = 2000 * this._streamRetryCount;
      setTimeout(() => {
        if (clientSearchKey === this.clientSearchKey) {
          console.debug(`[handleStreamError] Retrying search after stream error (attempt ${this._streamRetryCount})`);
          this.fetchSearchResults(clientSearchKey, this.params)
            .finally(() => {
              if (clientSearchKey === this.clientSearchKey && this._downloadedResults.size === 0) {
                runInAction(() => this.isFetchingMore = false);
              }
            });
        }
      }, delay);
      return;
    }

    runInAction(() => {
      this.searchStreamId = null;
      this._streamRetryCount = 0;
      this.awaitingInitialResults = false;
      this.retainResultsLayout = false;
      this.isFetchingMore = false;
      const errorMessage = error.message || "Search stream error";
      this.lastError = errorMessage;
      this.searchResultAnnotation = errorMessage;
    });
    toast.error("Search failed — please try again", {toastId: "search-stream-error"});
  };

  fetchDsConfigMetadata = async (dsConfig) => {
    if (this.fetchingDsConfigMetadata) {
      return this.fetchingDsConfigMetadata;
    }
    return this.fetchingDsConfigMetadata = this.appStatusStore
      .fetchDatasetMetadata(this.authData, dsConfig)
      .then(datasets => {
          runInAction(() => this.dsConfigDatasets = datasets);
          return this.appStatusStore.fetchCategoryMetadata(this.authData, dsConfig).then(md => {
            this.updateMetadata(md);
          })
        })
      .catch(error => {
        this.clear("Could not obtain graph configuration info");
      })
      .finally(() => runInAction(() => this.fetchingDsConfigMetadata = false));
  };

  updateMetadata = (md) => {
    this.metadata = md;
    this.templates = extractTemplates(md);
    // filter non-category entries such as _templates, _default
    this.categories = Object.keys(md)
      .filter(key => !key.startsWith("_"))
      .reduce((acc, key) => {
        acc[key] = md[key];
        return acc;
      }, {});
    // Only reset currentCategory if it's not in the new categories AND it's not being loaded from a URL
    // (indicated by currentCategory being set to something other than DEFAULT_CATEGORY_KEY before metadata loads)
    const validCategory = new Set(Object.keys(this.categories)).has(this.currentCategory) || this.currentCategory === "graph";
    if (!validCategory) {
      console.debug(`[updateMetadata] Current category "${this.currentCategory}" not in loaded categories, resetting to default`);
      this.currentCategory = DEFAULT_CATEGORY_KEY;
    }
  };
}

export default SearchSessionStore;
